import React, { useEffect } from 'react';
import classNames from 'classnames';
import { Control, Controller, RegisterOptions } from 'react-hook-form';
import styles from './RadioButtonControl.module.scss';
import RadioButton from '../RadioButton/RadioButton';

export interface RadioButtonControlProps {
  options: {
    label: string;
    value: string;
  }[];
  onChange?: (value: string) => void;
  defaultValue?: string;
  disabled?: boolean;
  className?: string;
}

const RadioButtonControl: React.FC<RadioButtonControlProps> = ({
  options = [],
  defaultValue,
  disabled,
  className,
  onChange,
}) => {
  const [selectedOption, setSelectedOption] = React.useState<string>(defaultValue || options[0].value);

  useEffect(() => {
    if (onChange) {
      onChange(selectedOption);
    }
  }, [selectedOption]);

  useEffect(() => {
    setSelectedOption(defaultValue || options[0].value);
  }, [defaultValue]);

  return (
    <div className={classNames(styles.root, className)}>
      {options &&
        options.map(option => (
          <RadioButton
            disabled={disabled}
            key={option.value}
            value={option.value}
            label={option.label}
            checked={selectedOption === option.value}
            onChange={() => setSelectedOption(option.value)}
          />
        ))}
    </div>
  );
};

export default RadioButtonControl;

interface ControlledRadioButtonControlProps extends Omit<RadioButtonControlProps, 'onChange' | 'defaultValue'> {
  control: Control<any>;
  name: string;
  rules?: RegisterOptions;
  defaultValue?: string;
}

export const ControlledRadioButtonControl: React.FC<ControlledRadioButtonControlProps> = ({
  control,
  name,
  rules,
  options,
  defaultValue,
  disabled,
  className,
}) => {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      defaultValue={defaultValue || (options[0] && options[0].value) || ''}
      render={({ field: { onChange, value } }) => (
        <RadioButtonControl
          options={options}
          defaultValue={value}
          disabled={disabled}
          className={className}
          onChange={onChange}
        />
      )}
    />
  );
};
